import type {
    FUSportsLivescoreCompetitionGroup,
    FUSportsLivescoreEvent,
    FUSportsLivescoreScore,
    FUSportsLivescoreStatus,
    SearchEntityResult,
} from "fansunited-data-layer";
import type { Labels } from "@/lib/labels/types";

/** Lowercase sport identifiers used across the livescore UI. Canonical values. */
export type LivescoreSport = "football" | "basketball" | "tennis" | "hockey" | "american-football";

/** Filter identifiers accepted across ticker, widget, and /livescore page. */
export type LivescoreFilter = "all" | "popular" | "upcoming" | "live" | "finished" | "my-leagues";

/**
 * Wire shape for anything backed by `getFansUnitedSportsLivescoreHydrated`.
 * Groups pass through as the endpoint returns them; entities are converted
 * from Map → Record for RSC / server-action serialisation.
 */
export interface LivescoreBoard {
    groups: FUSportsLivescoreCompetitionGroup[];
    entities: Record<string, SearchEntityResult>;
}

type TeamOrPlayerEntity = Extract<SearchEntityResult, { entityType: "team" | "athlete" }>;
type CompetitionEntity = Extract<SearchEntityResult, { entityType: "competition" }>;

/** Narrow a hydrated entity lookup to a team / athlete competitor. */
export function asCompetitor(
    entities: Record<string, SearchEntityResult>,
    id: string | undefined
): TeamOrPlayerEntity | undefined {
    if (!id) return undefined;
    const entity = entities[id];
    if (!entity) return undefined;
    return entity.entityType === "team" || entity.entityType === "athlete" ? entity : undefined;
}

/** Narrow a hydrated entity lookup to a competition. */
export function asCompetition(
    entities: Record<string, SearchEntityResult>,
    id: string | undefined
): CompetitionEntity | undefined {
    if (!id) return undefined;
    const entity = entities[id];
    if (!entity) return undefined;
    return entity.entityType === "competition" ? entity : undefined;
}

/** Aggregate score keys returned by the API — not part of per-period breakdown. */
const AGGREGATE_SCORE_KEYS = new Set(["result", "full_time", "half_time"]);

/**
 * Pull a specific score line by key from the flat score array.
 * Returns null when the key is absent (e.g. before kickoff).
 */
export function getScoreByKey(
    score: FUSportsLivescoreScore[] | undefined,
    key: string
): { competitorOne: string; competitorTwo: string } | null {
    if (!score?.length) return null;
    const line = score.find((s) => s.key === key);
    return line ? { competitorOne: line.competitorOne, competitorTwo: line.competitorTwo } : null;
}

/**
 * Running/final score used everywhere as "the" score for a match:
 * prefers `result`, falls back to `full_time`, then the first entry.
 * Returns both competitor sides in one shot.
 */
export function getResultScore(
    score: FUSportsLivescoreScore[] | undefined
): { competitorOne: string | null; competitorTwo: string | null } {
    if (!score?.length) return { competitorOne: null, competitorTwo: null };
    const line = score.find((s) => s.key === "result") ?? score.find((s) => s.key === "full_time") ?? score[0];
    return { competitorOne: line?.competitorOne ?? null, competitorTwo: line?.competitorTwo ?? null };
}

export interface PeriodScoreEntry {
    key: string;
    label: string;
    c1: number | null;
    c2: number | null;
}

/** Convert a score value to number, returning null for non-numeric or missing. */
function toNum(v: string | null | undefined): number | null {
    if (v == null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
}

/**
 * OT/penalty keys the data layer passes through unchanged (it only does
 * snake_case → camelCase; no dedup, no OT normalization). We treat them
 * specially so an American football finished-in-OT match doesn't render a
 * bogus 5th "regulation" quarter labelled Q5.
 */
function periodKind(key: string): "regulation" | "overtime" | "penalty" {
    const k = key.toLowerCase();
    if (k.includes("penalt") || k === "so" || k.startsWith("shootout")) return "penalty";
    if (k.includes("overtime") || k.includes("extra_time") || k.startsWith("ot") || k === "et") return "overtime";
    return "regulation";
}

/**
 * Extract per-period entries (basketball quarters, tennis sets, etc.) from a
 * score array. Aggregate keys (`result`, `full_time`, `half_time`) and tennis
 * tiebreak sub-scores (`set1_tiebreak`, …) are skipped.
 *
 * The livescore endpoint returns score entries in arbitrary order — that's
 * why a real 5-set match sometimes surfaces as "S3 S2 S5 S4 S1". We sort by
 * the numeric suffix of the key (`set2` → 2, `q3` → 3) before rendering, so
 * the columns are always chronological. Labels use the same suffix so a
 * match that only exposes sets 1 and 3 renders "S1" and "S3" (never shifted
 * to "S1"/"S2"). Falls back to insertion order when the key has no digits.
 *
 * `maxRegulationCount` caps the number of **regulation** periods only
 * (basketball = 4 quarters, hockey = 3 periods, tennis = 5 sets). Overtime
 * and penalty entries always flow through, so a basketball game that goes
 * to OT still renders Q1-Q4 + OT, not just Q1-Q4.
 */
export function getPeriodScores(
    score: FUSportsLivescoreScore[] | undefined,
    labelPrefix: string,
    maxRegulationCount?: number
): PeriodScoreEntry[] {
    if (!score?.length) return [];
    const periods = score
        .filter((s) => !AGGREGATE_SCORE_KEYS.has(s.key) && !s.key.endsWith("_tiebreak"))
        .map((s, insertionIndex) => {
            const c1 = toNum(s.competitorOne);
            const c2 = toNum(s.competitorTwo);
            const kind = periodKind(s.key);
            const numMatch = s.key.match(/(\d+)/);
            const sortIndex = numMatch ? Number(numMatch[1]) : Number.MAX_SAFE_INTEGER;
            return { key: s.key, c1, c2, kind, sortIndex, insertionIndex };
        })
        // Skip periods where neither side has a numeric score — they're
        // placeholder entries the API sometimes emits for unplayed sets.
        .filter((p) => p.c1 !== null || p.c2 !== null)
        // Sort regulation periods by their numeric suffix first, then OT, then
        // penalties. Within each kind, preserve numeric order or fall back to
        // insertion order for unnumbered keys.
        .sort((a, b) => {
            const kindOrder = { regulation: 0, overtime: 1, penalty: 2 };
            if (kindOrder[a.kind] !== kindOrder[b.kind]) return kindOrder[a.kind] - kindOrder[b.kind];
            return a.sortIndex - b.sortIndex || a.insertionIndex - b.insertionIndex;
        });

    // Dedup for OT/penalty only: AF emits both `ot1` and `extra_time` for the
    // same OT period with identical scores — one would render as a phantom
    // extra column. Regulation periods come as distinct `p1`/`p2`/`p3` keys
    // that are legitimately different even when the scores match (hockey can
    // easily have P1=0-1 and P2=0-1), so we don't dedup them.
    const seenPostRegulation = new Set<string>();

    const out: PeriodScoreEntry[] = [];
    let regulationCount = 0;
    let fallbackIndex = 0;
    let otIndex = 0;
    let penaltyIndex = 0;
    for (const p of periods) {
        let label: string;
        if (p.kind === "overtime") {
            const signature = `overtime:${p.c1}:${p.c2}`;
            if (seenPostRegulation.has(signature)) continue;
            seenPostRegulation.add(signature);
            otIndex += 1;
            label = otIndex === 1 ? "OT" : `OT${otIndex}`;
        } else if (p.kind === "penalty") {
            const signature = `penalty:${p.c1}:${p.c2}`;
            if (seenPostRegulation.has(signature)) continue;
            seenPostRegulation.add(signature);
            penaltyIndex += 1;
            label = "P";
        } else {
            if (maxRegulationCount !== undefined && regulationCount >= maxRegulationCount) continue;
            regulationCount += 1;
            fallbackIndex += 1;
            label = p.sortIndex === Number.MAX_SAFE_INTEGER
                ? `${labelPrefix}${fallbackIndex}`
                : `${labelPrefix}${p.sortIndex}`;
        }
        out.push({ key: p.key, label, c1: p.c1, c2: p.c2 });
    }
    return out;
}

/** True when the group's sport tag matches the given lowercase sport id. */
export function groupMatchesSport(group: FUSportsLivescoreCompetitionGroup, sport: LivescoreSport): boolean {
    return group.sport.toLowerCase() === sport;
}

/** Flatten all events across the given groups, annotating each with its sport + competitionId. */
export interface FlatLivescoreEvent {
    event: FUSportsLivescoreEvent;
    competitionId: string;
    sport: string;
}

export function flattenGroups(groups: FUSportsLivescoreCompetitionGroup[]): FlatLivescoreEvent[] {
    const out: FlatLivescoreEvent[] = [];
    for (const group of groups) {
        for (const event of group.events) {
            out.push({ event, competitionId: group.competitionId, sport: group.sport });
        }
    }
    return out;
}

/**
 * `currentMinute` returned by the FU livescore endpoint restarts at 0 at the
 * start of every match phase (halftime → 0, second half kickoff → 0, ...).
 * For football we need an aggregate display: HT during the break, 45+n
 * during the second half, etc. Vocabulary matches Sportal365's `sub_type`
 * codes. Unknown codes fall back to the raw minute so a schema change
 * degrades to the pre-fix behaviour rather than blanking the clock.
 */
const LOGGED_UNKNOWN_FOOTBALL_STATUS = new Set<string>();

export function formatFootballLiveMinute(
    status: FUSportsLivescoreStatus | undefined,
    currentMinute: number | undefined,
    labels: Labels["livescore"]
): string | null {
    if (!status) return null;
    const minute = currentMinute ?? 0;
    const raw = `${minute}'`;
    // Sportal365 uses `1st_half` / `2nd_half`; extra-time / shootout codes are
    // speculative and will emit the debug warn below if the wire values differ.
    switch (status.subType) {
        case "1st_half":
        case "first_half":
            return raw;
        case "halftime":
        case "half_time":
            return labels.ht;
        case "2nd_half":
        case "second_half":
            return `${45 + minute}'`;
        case "awaiting_extra_time":
            return `90'`;
        case "1st_extra":
        case "extra_time_1st_half":
        case "extra_time_first_half":
            return `${90 + minute}'`;
        case "extra_time_halftime":
        case "extra_halftime":
            return `${labels.ht} (ET)`;
        case "2nd_extra":
        case "extra_time_2nd_half":
        case "extra_time_second_half":
            return `${105 + minute}'`;
        case "awaiting_penalty_shootout":
        case "penalty_shootout":
        case "shootout":
            return labels.pen;
        case "interrupted":
            return labels.paused;
        default:
            if (status.subType && !LOGGED_UNKNOWN_FOOTBALL_STATUS.has(status.subType)) {
                LOGGED_UNKNOWN_FOOTBALL_STATUS.add(status.subType);
                console.warn("[formatFootballLiveMinute] unknown football status.subType", status);
            }
            return raw;
    }
}
